import api from './api'
import type { ProposalRecord } from '@/types'

export const proposalsService = {
  // NOTE: `take` default is intentionally large. The list page paginates client-side
  // (slices the array by PAGE_SIZE=25), so the request must fetch enough records to
  // cover every page the user can navigate to. The old default of 50 capped the UI
  // at exactly 2 pages regardless of how many proposals the tenant actually had.
  // TODO: switch to server-side pagination — refetch on page change using the API's
  // `total` field for the page count, then drop this default back to a sane window.
  async list(skip = 0, take = 1000) {
    const params = new URLSearchParams()
    params.set('skip', String(skip))
    params.set('take', String(take))
    const { data } = await api.get(`/proposals?${params}`)
    return data.data as { proposals: ProposalRecord[]; total: number }
  },

  async getById(id: string) {
    const { data } = await api.get(`/proposals/${id}`)
    return data.data as ProposalRecord
  },

  async create(estimateId: string) {
    const { data } = await api.post('/proposals', { estimateId })
    return data.data as ProposalRecord
  },

  async getByEstimate(estimateId: string) {
    const { data } = await api.get(`/proposals/estimate/${estimateId}`)
    return data.data as ProposalRecord | null
  },

  async send(proposalId: string, emails: string[]) {
    const { data } = await api.post(`/proposals/${proposalId}/send`, { emails })
    return data.data
  },

  async markAsSent(proposalId: string) {
    const { data } = await api.post(`/proposals/${proposalId}/mark-sent`)
    return data.data
  },

  async markAsSigned(proposalId: string, signerName: string, signedDate: string) {
    const { data } = await api.post(`/proposals/${proposalId}/mark-signed`, { signerName, signedDate })
    return data.data
  },

  async downloadPdf(proposalId: string): Promise<Blob> {
    const { data } = await api.get(`/proposals/${proposalId}/pdf`, { responseType: 'blob' })
    return data as Blob
  },

  async downloadPreviewPdf(template?: string): Promise<Blob> {
    const params = template ? `?template=${template}` : '';
    const { data } = await api.get(`/proposals/preview-pdf${params}`, { responseType: 'blob' })
    return data as Blob
  },

  // ============ REVISIONS ============

  async startRevision(proposalId: string) {
    const { data } = await api.post(`/proposals/${proposalId}/start-revision`)
    return data.data as { proposal: ProposalRecord; revisionEstimate: any }
  },

  async finalizeRevision(proposalId: string) {
    const { data } = await api.post(`/proposals/${proposalId}/finalize-revision`)
    return data.data as ProposalRecord
  },

  async cancelRevision(proposalId: string) {
    const { data } = await api.post(`/proposals/${proposalId}/cancel-revision`)
    return data.data as ProposalRecord
  },

  // ============ PUBLIC (CUSTOMER-FACING) ============

  async getPublic(shareableLink: string) {
    const { data } = await api.get(`/proposals/public/${shareableLink}`)
    return data.data
  },

  async signPublic(shareableLink: string, signatureName: string, signatureDataUrl: string) {
    const { data } = await api.post(`/proposals/public/${shareableLink}/sign`, {
      signatureName,
      signatureDataUrl,
    })
    return data.data
  },

  async downloadPublicPdf(shareableLink: string): Promise<Blob> {
    const { data } = await api.get(`/proposals/public/${shareableLink}/pdf`, { responseType: 'blob' })
    return data as Blob
  },

  async remove(proposalId: string) {
    await api.delete(`/proposals/${proposalId}`)
  },
}
